import { useCallback, useEffect, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import { AreaTableView, AreaView, FloorDecorElement, areasService } from '../../../../services/areas/areas.service';
import { clamp, normalizeArea, removeById } from '../../core/pos-shell-page.helpers';
import { findFreeTablePosition, findReusableDisabledTable, nextTableNumberAfter } from '../../core/pos-shell-page.forms';
import { type TableFormState, type TableLayoutState } from '../../core/pos-shell-page.types';

type UseAreaTableEditingParams = {
  employeeSession: string | null;
  isManager: boolean;
  tenantId: string;
  areaMode: 'view' | 'edit';
  selectedAreaId: number | null;
  editingTableId: number | null;
  copiedTable: AreaTableView | null;
  settingsAreasRef: MutableRefObject<AreaView[]>;
  tableMutationInFlightRef: MutableRefObject<boolean>;
  setSettingsAreas: Dispatch<SetStateAction<AreaView[]>>;
  setActiveAreas: Dispatch<SetStateAction<AreaView[]>>;
  setSelectedAreaId: Dispatch<SetStateAction<number | null>>;
  setEditingTableId: Dispatch<SetStateAction<number | null>>;
  setTableForm: Dispatch<SetStateAction<TableFormState>>;
  setIsSavingTable: Dispatch<SetStateAction<boolean>>;
  setCopiedTable: Dispatch<SetStateAction<AreaTableView | null>>;
  setError: Dispatch<SetStateAction<string | null>>;
  resetTableForm: () => void;
};

/**
 * Architectural summary: owns ALL floor-plan edit-mode mutation behavior — both
 * area-level (create / rename / activate / deactivate) and table-level (the
 * diff-only PATCH path plus the toolbar-facing rename/shape/duplicate/delete and
 * clipboard paste handlers) — so the presentational pieces (AreaBar,
 * TableEditToolbar, the drag/resize canvas, PosAreasSection) stay free of
 * networking and state-merge logic (Rule 32/36). `persistTableUpdate` is the
 * single diff-only PATCH path shared by drag, resize, form-save, rename, and
 * shape-change; every mutation goes out the same way and the canonical row
 * returns via the 'area.upserted' / 'table.upserted' / 'table.removed' SSE echo
 * (Rules 20/23/27).
 */
export function useAreaTableEditing({
  employeeSession,
  isManager,
  tenantId,
  areaMode,
  selectedAreaId,
  editingTableId,
  copiedTable,
  settingsAreasRef,
  tableMutationInFlightRef,
  setSettingsAreas,
  setActiveAreas,
  setSelectedAreaId,
  setEditingTableId,
  setTableForm,
  setIsSavingTable,
  setCopiedTable,
  setError,
  resetTableForm,
}: UseAreaTableEditingParams) {
  // PATCHes only changed table fields. Drag/resize callers pass baseline geometry
  // so diffs remain accurate after optimistic preview updates.
  const persistTableUpdate = useCallback(async (
    areaId: number,
    tableId: number,
    next: TableLayoutState,
    baseline?: TableLayoutState | null,
  ) => {
    if (!employeeSession || !isManager) {
      return;
    }

    const normalized: TableLayoutState = {
      ...next,
      number: next.number.trim(),
      positionX: clamp(next.positionX, 0, 5000),
      positionY: clamp(next.positionY, 0, 5000),
      width: clamp(next.width, 40, 2000),
      height: clamp(next.height, 40, 2000),
    };

    // Diff against the caller-supplied baseline, not live state: the drag path mutates
    // settingsAreas (and the ref) on every mousemove, so by mouseUp the ref already equals
    // the final preview — diffing against it would be empty and skip the PATCH. Form-edit
    // callers pass no baseline and correctly fall back to the stored row.
    const sourceTable = baseline ?? settingsAreasRef.current
      .find((area) => area.id === areaId)
      ?.tables.find((table) => table.id === tableId);
    const updatePayload: Partial<TableLayoutState> = {};
    if (!sourceTable || normalized.number !== sourceTable.number) {
      updatePayload.number = normalized.number;
    }
    if (!sourceTable || normalized.shape !== sourceTable.shape) {
      updatePayload.shape = normalized.shape;
    }
    if (!sourceTable || normalized.positionX !== sourceTable.positionX) {
      updatePayload.positionX = normalized.positionX;
    }
    if (!sourceTable || normalized.positionY !== sourceTable.positionY) {
      updatePayload.positionY = normalized.positionY;
    }
    if (!sourceTable || normalized.width !== sourceTable.width) {
      updatePayload.width = normalized.width;
    }
    if (!sourceTable || normalized.height !== sourceTable.height) {
      updatePayload.height = normalized.height;
    }
    if (Object.keys(updatePayload).length === 0) {
      // No changes detected — skip the API call.
      return;
    }

    await areasService.updateTable(areaId, tableId, updatePayload, employeeSession);
    const patchTable = (existing: AreaTableView): AreaTableView => ({
      ...existing,
      ...(updatePayload.number !== undefined ? { number: updatePayload.number } : {}),
      ...(updatePayload.shape !== undefined ? { shape: updatePayload.shape } : {}),
      ...(updatePayload.positionX !== undefined ? { positionX: updatePayload.positionX } : {}),
      ...(updatePayload.positionY !== undefined ? { positionY: updatePayload.positionY } : {}),
      ...(updatePayload.width !== undefined ? { width: updatePayload.width } : {}),
      ...(updatePayload.height !== undefined ? { height: updatePayload.height } : {}),
    });
    const patchAreas = (areas: AreaView[]): AreaView[] =>
      areas.map((area) => {
        if (area.id !== areaId) {
          return area;
        }
        return {
          ...area,
          tables: (Array.isArray(area.tables) ? area.tables : []).map((table) =>
            table.id === tableId ? patchTable(table) : table,
          ),
        };
      });
    setSettingsAreas(patchAreas);
    setActiveAreas(patchAreas);
  }, [employeeSession, isManager, settingsAreasRef, setSettingsAreas, setActiveAreas]);

  // Commits an inline table rename from the floating edit toolbar. Trims input,
  // rejects blank/unchanged (no PATCH), and routes through persistTableUpdate.
  const handleRenameTable = useCallback(async (rawName: string) => {
    if (!isManager || selectedAreaId === null || editingTableId === null) {
      return;
    }
    const sourceTable = settingsAreasRef.current
      .find((area) => area.id === selectedAreaId)
      ?.tables.find((table) => table.id === editingTableId);
    if (!sourceTable) {
      return;
    }
    const nextNumber = rawName.trim();
    if (!nextNumber || nextNumber === sourceTable.number) {
      // Blank or unchanged — no PATCH; UI reverts to the stored number.
      return;
    }
    try {
      await persistTableUpdate(selectedAreaId, editingTableId, {
        number: nextNumber,
        shape: sourceTable.shape,
        positionX: sourceTable.positionX,
        positionY: sourceTable.positionY,
        width: sourceTable.width,
        height: sourceTable.height,
      });
    } catch (renameError: unknown) {
      setError(renameError instanceof Error ? renameError.message : 'Failed renaming table');
    }
  }, [editingTableId, isManager, persistTableUpdate, selectedAreaId, settingsAreasRef, setError]);

  // Switches the selected table's shape from the floating edit toolbar. No-ops
  // when unchanged; otherwise goes out as a diff-only PATCH (canonical via SSE).
  const handleChangeShape = useCallback(async (nextShape: AreaTableView['shape']) => {
    if (!isManager || selectedAreaId === null || editingTableId === null) {
      return;
    }
    const sourceTable = settingsAreasRef.current
      .find((area) => area.id === selectedAreaId)
      ?.tables.find((table) => table.id === editingTableId);
    if (!sourceTable || sourceTable.shape === nextShape) {
      return;
    }
    try {
      await persistTableUpdate(selectedAreaId, editingTableId, {
        number: sourceTable.number,
        shape: nextShape,
        positionX: sourceTable.positionX,
        positionY: sourceTable.positionY,
        width: sourceTable.width,
        height: sourceTable.height,
      });
    } catch (shapeError: unknown) {
      setError(shapeError instanceof Error ? shapeError.message : 'Failed changing table shape');
    }
  }, [editingTableId, isManager, persistTableUpdate, selectedAreaId, settingsAreasRef, setError]);

  // Reuses a disabled table's row for `number` if one exists in this area, else creates a new one.
  const createOrReactivateTable = useCallback(async (
    currentTables: AreaTableView[],
    areaId: number,
    number: string,
    shape: AreaTableView['shape'],
    positionX: number,
    positionY: number,
    width: number,
    height: number,
  ): Promise<number> => {
    const reusable = findReusableDisabledTable(currentTables, number);
    if (reusable) {
      await areasService.updateTable(
        areaId,
        reusable.id,
        { number, shape, positionX, positionY, width, height },
        employeeSession as string,
      );
      return reusable.id;
    }
    const created = await areasService.createTable(areaId, { number, shape }, employeeSession as string);
    await areasService.updateTable(
      areaId,
      created.id,
      { positionX, positionY, width, height },
      employeeSession as string,
    );
    return created.id;
  }, [employeeSession]);

  // Duplicates the selected table: same shape/size, numbered source+1 (tenant-wide fallback).
  const handleDuplicateTable = useCallback(async () => {
    if (editingTableId === null || selectedAreaId === null || !employeeSession || !isManager) {
      return;
    }
    if (tableMutationInFlightRef.current) {
      return;
    }
    const currentTables = settingsAreasRef.current.find((a) => a.id === selectedAreaId)?.tables ?? [];
    const source = currentTables.find((t) => t.id === editingTableId);
    if (!source) {
      return;
    }
    tableMutationInFlightRef.current = true;
    const allTables = settingsAreasRef.current.flatMap((area) => area.tables ?? []);
    const copyName = nextTableNumberAfter(source.number, allTables);
    const freePos = findFreeTablePosition(currentTables.filter((t) => t.isActive), source.width, source.height);
    setIsSavingTable(true);
    try {
      const tableId = await createOrReactivateTable(
        currentTables,
        selectedAreaId,
        copyName,
        source.shape,
        freePos.x,
        freePos.y,
        source.width,
        source.height,
      );
      setEditingTableId(tableId);
      setTableForm({ number: copyName, shape: source.shape });
    } catch (duplicateError: unknown) {
      setError(duplicateError instanceof Error ? duplicateError.message : 'Failed duplicating table');
    } finally {
      setIsSavingTable(false);
      tableMutationInFlightRef.current = false;
    }
  }, [
    editingTableId,
    selectedAreaId,
    employeeSession,
    isManager,
    settingsAreasRef,
    tableMutationInFlightRef,
    createOrReactivateTable,
    setEditingTableId,
    setTableForm,
    setIsSavingTable,
    setError,
  ]);

  // Creates a copy of the currently-copied table in the selected area (Ctrl+V).
  const handlePasteTable = useCallback(async () => {
    if (!copiedTable || selectedAreaId === null || !employeeSession || !isManager) {
      return;
    }
    if (tableMutationInFlightRef.current) {
      return;
    }
    tableMutationInFlightRef.current = true;
    const currentTables = settingsAreasRef.current.find((a) => a.id === selectedAreaId)?.tables ?? [];
    const allTables = settingsAreasRef.current.flatMap((area) => area.tables ?? []);
    const copyName = nextTableNumberAfter(copiedTable.number, allTables);
    const freePos = findFreeTablePosition(currentTables.filter((t) => t.isActive), copiedTable.width, copiedTable.height);
    setIsSavingTable(true);
    try {
      await createOrReactivateTable(
        currentTables,
        selectedAreaId,
        copyName,
        copiedTable.shape,
        freePos.x,
        freePos.y,
        copiedTable.width,
        copiedTable.height,
      );
    } catch (pasteError: unknown) {
      setError(pasteError instanceof Error ? pasteError.message : 'Failed pasting table');
    } finally {
      setIsSavingTable(false);
      tableMutationInFlightRef.current = false;
    }
  }, [
    copiedTable,
    selectedAreaId,
    employeeSession,
    isManager,
    settingsAreasRef,
    tableMutationInFlightRef,
    createOrReactivateTable,
    setIsSavingTable,
    setError,
  ]);

  // Soft-removes the selected table and mirrors removal locally; SSE 'table.removed'
  // confirms on all devices.
  const handleDeleteTable = useCallback(async () => {
    if (editingTableId === null || selectedAreaId === null || !employeeSession || !isManager) {
      return;
    }
    if (!window.confirm('האם להסיר את השולחן מהמפה?')) {
      return;
    }
    if (tableMutationInFlightRef.current) {
      return;
    }
    tableMutationInFlightRef.current = true;
    setIsSavingTable(true);
    try {
      await areasService.deleteTable(selectedAreaId, editingTableId, employeeSession);
      const removeFromArea = (areas: AreaView[]) =>
        areas.map((area) => {
          if (area.id !== selectedAreaId) {
            return area;
          }
          return { ...area, tables: removeById(area.tables, editingTableId) };
        });
      setSettingsAreas(removeFromArea);
      setActiveAreas(removeFromArea);
      resetTableForm();
    } catch (deleteError: unknown) {
      setError(deleteError instanceof Error ? deleteError.message : 'Failed deleting table');
    } finally {
      setIsSavingTable(false);
      tableMutationInFlightRef.current = false;
    }
  }, [
    editingTableId,
    selectedAreaId,
    employeeSession,
    isManager,
    tableMutationInFlightRef,
    setSettingsAreas,
    setActiveAreas,
    setIsSavingTable,
    setError,
    resetTableForm,
  ]);

  // --- Area-level mutations (AreaBar) ---------------------------------------

  // Creates an area directly from the AreaBar inline field (no form draft).
  // Backend returns { id } only; canonical row arrives via 'area.upserted' SSE echo. Auto-select the new area.
  const handleCreateAreaByName = useCallback(async (name: string) => {
    if (!employeeSession || !isManager) {
      return;
    }
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      return;
    }
    setError(null);
    try {
      const { id: newAreaId } = await areasService.create(tenantId, { name: trimmed }, employeeSession);
      setSelectedAreaId(newAreaId);
    } catch (createError: unknown) {
      setError(createError instanceof Error ? createError.message : 'Failed saving area');
    }
  }, [employeeSession, isManager, tenantId, setSelectedAreaId, setError]);

  // Renames an area directly from the AreaBar inline field. Diff PATCH (name only);
  // optimistic local patch, SSE echo overwrites with canonical row within ~50ms.
  const handleRenameAreaByName = useCallback(async (areaId: number, name: string) => {
    if (!employeeSession || !isManager) {
      return;
    }
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      return;
    }
    setError(null);
    try {
      await areasService.update(areaId, { name: trimmed }, employeeSession);
      setSettingsAreas((previous) =>
        previous.map((item) => (item.id === areaId ? normalizeArea({ ...item, name: trimmed }) : item)),
      );
    } catch (renameError: unknown) {
      setError(renameError instanceof Error ? renameError.message : 'Failed saving area');
    }
  }, [employeeSession, isManager, setSettingsAreas, setError]);

  const handleUpdateAreaFloorDecor = useCallback(async (areaId: number, floorDecor: FloorDecorElement[]) => {
    if (!employeeSession || !isManager) {
      return;
    }
    setError(null);
    try {
      await areasService.update(areaId, { floorDecor }, employeeSession);
      const patchDecor = (areas: AreaView[]) =>
        areas.map((item) => (item.id === areaId ? normalizeArea({ ...item, floorDecor }) : item));
      setSettingsAreas(patchDecor);
      setActiveAreas(patchDecor);
    } catch (updateError: unknown) {
      setError(updateError instanceof Error ? updateError.message : 'Failed updating floor elements');
    }
  }, [employeeSession, isManager, setSettingsAreas, setActiveAreas, setError]);

  // Optimistic swap; reverted on failure since it's its own inverse (no SSE echo without a real mutation).
  const handleReorderAreas = useCallback(async (areaId: number, otherAreaId: number) => {
    if (!employeeSession || !isManager) {
      return;
    }
    setError(null);
    const swapLocally = (areas: AreaView[]): AreaView[] => {
      const a = areas.find((area) => area.id === areaId);
      const b = areas.find((area) => area.id === otherAreaId);
      if (!a || !b) {
        return areas;
      }
      return areas.map((area) => {
        if (area.id === areaId) return { ...area, sortIndex: b.sortIndex };
        if (area.id === otherAreaId) return { ...area, sortIndex: a.sortIndex };
        return area;
      });
    };
    setSettingsAreas(swapLocally);
    setActiveAreas(swapLocally);
    try {
      await areasService.reorder(areaId, otherAreaId, employeeSession);
    } catch (reorderError: unknown) {
      setSettingsAreas(swapLocally);
      setActiveAreas(swapLocally);
      setError(reorderError instanceof Error ? reorderError.message : 'Failed reordering areas');
    }
  }, [employeeSession, isManager, setSettingsAreas, setActiveAreas, setError]);

  // Deactivates an area from edit mode; drops it from the active list and marks it inactive in settings.
  const deactivateArea = useCallback(async (area: AreaView) => {
    if (!employeeSession || !isManager) {
      return;
    }
    if (!window.confirm(`Deactivate ${area.name}?`)) {
      return;
    }
    setError(null);
    try {
      await areasService.deactivate(area.id, employeeSession);
      // Backend returns 204; SSE echo will mark area inactive in both lists. Patch locally for instant feedback.
      setSettingsAreas((previous) =>
        previous.map((item) => (item.id === area.id ? { ...item, isActive: false } : item)),
      );
      setActiveAreas((previous) => previous.filter((item) => item.id !== area.id));
    } catch (deactivateError: unknown) {
      setError(deactivateError instanceof Error ? deactivateError.message : 'Failed deactivating area');
    }
  }, [employeeSession, isManager, setSettingsAreas, setActiveAreas, setError]);

  // Reactivates an area from edit mode; marks it active in settings (active list refilled via SSE echo).
  const activateArea = useCallback(async (area: AreaView) => {
    if (!employeeSession || !isManager) {
      return;
    }
    if (!window.confirm(`Activate ${area.name}?`)) {
      return;
    }
    setError(null);
    try {
      await areasService.activate(area.id, employeeSession);
      // Backend returns 204; SSE echo will deliver the activated row. Patch settings list locally for instant feedback.
      setSettingsAreas((previous) =>
        previous.map((item) => (item.id === area.id ? { ...item, isActive: true } : item)),
      );
    } catch (activateError: unknown) {
      setError(activateError instanceof Error ? activateError.message : 'Failed activating area');
    }
  }, [employeeSession, isManager, setSettingsAreas, setError]);

  // Toggles the active/inactive state of the area selected in the AreaBar.
  const handleAreaActiveToggle = useCallback((area: AreaView) => {
    void (area.isActive ? deactivateArea(area) : activateArea(area));
  }, [activateArea, deactivateArea]);

  // --- Add table (palette) --------------------------------------------------

  // Places a table with the given number/shape/size at the clicked canvas position, then selects it.
  const handleAddTable = useCallback(async (number: string, shape: AreaTableView['shape'], positionX: number, positionY: number, width: number, height: number) => {
    if (!employeeSession || !isManager || selectedAreaId === null) {
      return;
    }
    if (tableMutationInFlightRef.current) {
      return;
    }
    tableMutationInFlightRef.current = true;
    setIsSavingTable(true);
    setError(null);
    try {
      const existingTables = settingsAreasRef.current.find((area) => area.id === selectedAreaId)?.tables ?? [];
      const tableId = await createOrReactivateTable(
        existingTables,
        selectedAreaId,
        number,
        shape,
        positionX,
        positionY,
        width,
        height,
      );
      setEditingTableId(tableId);
      setTableForm({ number, shape });
    } catch (addError: unknown) {
      setError(addError instanceof Error ? addError.message : 'Failed adding table');
    } finally {
      setIsSavingTable(false);
      tableMutationInFlightRef.current = false;
    }
  }, [
    employeeSession,
    isManager,
    selectedAreaId,
    settingsAreasRef,
    createOrReactivateTable,
    tableMutationInFlightRef,
    setEditingTableId,
    setTableForm,
    setIsSavingTable,
    setError,
  ]);

  // --- Clipboard (copy/paste) -----------------------------------------------

  // Copies the currently selected table into the clipboard slot.
  const handleCopyTable = useCallback(() => {
    if (editingTableId === null || selectedAreaId === null) {
      return;
    }
    const table = settingsAreasRef.current
      .find((area) => area.id === selectedAreaId)
      ?.tables.find((candidate) => candidate.id === editingTableId);
    if (table) {
      setCopiedTable(table);
    }
  }, [editingTableId, selectedAreaId, settingsAreasRef, setCopiedTable]);

  // Ctrl+C copies the selected table; Ctrl+V pastes the clipboard table into the active area.
  // Bound only in edit mode for managers. The handler reads settingsAreasRef.current so the
  // listener does not re-bind on every SSE delta / drag frame (Rule 28).
  useEffect(() => {
    if (areaMode !== 'edit' || !isManager) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.ctrlKey) {
        return;
      }
      if (event.key === 'c' && editingTableId !== null && selectedAreaId !== null) {
        event.preventDefault();
        handleCopyTable();
      }
      if (event.key === 'v' && copiedTable !== null) {
        event.preventDefault();
        void handlePasteTable();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [areaMode, isManager, editingTableId, selectedAreaId, copiedTable, handleCopyTable, handlePasteTable]);

  return {
    persistTableUpdate,
    handleRenameTable,
    handleChangeShape,
    handleDuplicateTable,
    handlePasteTable,
    handleDeleteTable,
    handleCreateAreaByName,
    handleRenameAreaByName,
    handleUpdateAreaFloorDecor,
    handleReorderAreas,
    handleAreaActiveToggle,
    handleCopyTable,
    handleAddTable,
  };
}
